import {
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart,
  Tooltip as RechartsTooltip,
} from "recharts";
import {
  Box,
  Card,
  SxProps,
  Theme,
  useTheme,
  Tooltip as MuiTooltip,
} from "@mui/material";
import CustomTypography from "../common/form/CustomTypography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AnalyticsGraphResponse } from "../../../models/Dashboard/Analytics";

export interface AppAreaChartData {
  labels: string[];
  values: any[];
}

export function parseAnalyticsGraphDataToAppAreaChartData(
  response: AnalyticsGraphResponse[]
): AppAreaChartData {
  if (!response || response.length === 0) {
    return { labels: [], values: [] };
  }

  const labels: string[] = [];
  const area_chart_map = new Map<string, any>();

  response.forEach((data_set) => {
    labels.push(data_set.label);

    data_set.values.forEach((data) => {
      if (!area_chart_map.has(data.label)) {
        area_chart_map.set(data.label, { label: data.label });
      }

      const numericValue = Number(data.value);
      area_chart_map.get(data.label)[data_set.label] = isNaN(numericValue)
        ? 0
        : numericValue;
    });
  });

  return { labels, values: Array.from(area_chart_map.values()) };
}

export interface AreaChartComponentProps {
  title: string;
  message?: string;
  data: AppAreaChartData;
  sx?: SxProps<Theme>;
  colors: string[];
  height?: number;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  const theme = useTheme();

  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper + "CC",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          padding: 1,
          boxShadow: theme.shadows[2],
        }}
      >
        <CustomTypography variant="body2" fontWeight={600}>
          {label}
        </CustomTypography>
        {payload.map((entry: any, index: number) => (
          <Box
            key={`tooltip-${index}`}
            sx={{
              display: "flex",
              alignItems: "center",
              marginTop: 0.5,
              color: entry.color,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                backgroundColor: entry.color,
                marginRight: 1,
              }}
            />
            <CustomTypography variant="body2">
              {entry.name}: {entry.value}
            </CustomTypography>
          </Box>
        ))}
      </Box>
    );
  }

  return null;
};

export default function AreaChartComponent({
  title,
  message,
  data,
  sx,
  colors,
  height = 400,
}: AreaChartComponentProps) {
  const theme = useTheme();
  const areaDataKeys = data.labels.map((label, index) => ({
    key: label,
    color: colors[index % colors.length],
  }));

  return (
    <Card sx={{ p: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <CustomTypography
          variant="heading4"
          color={theme.palette.customColors.blackBorder}
          style={{ marginBottom: 24 }}
        >
          {title}
        </CustomTypography>
        {message && (
          <MuiTooltip title={message}>
            <InfoOutlinedIcon
              sx={{ cursor: "pointer", color: theme.palette.grey[600] }}
            />
          </MuiTooltip>
        )}
      </Box>

      {data.values.length > 0 && (
        <Box sx={{ ...sx, overflowY: "hidden" }}>
          <Box
            sx={{
              width:
                data.values.length > 7
                  ? `${data.values.length * 100}px`
                  : "100%",
            }}
          >
            <ResponsiveContainer width="100%" height={height}>
              <ComposedChart
                data={data.values}
                margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
              >
                <XAxis
                  axisLine={false}
                  dataKey="label"
                  fontSize={"0.8rem"}
                  scale="point"
                  padding={{ left: 20, right: 20 }}
                />
                <YAxis axisLine={false} fontSize={"0.8rem"} />
                <CartesianGrid horizontal vertical={false} />
                <RechartsTooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="top"
                  align="left"
                  wrapperStyle={{ paddingBottom: "1rem", fontSize: "0.8rem" }}
                />

                {areaDataKeys.map((keyConfig) => (
                  <Area
                    key={keyConfig.key}
                    type="monotone"
                    dataKey={keyConfig.key}
                    fillOpacity={0.2}
                    stroke={keyConfig.color}
                    fill={keyConfig.color}
                    activeDot={{ r: 6 }}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      )}
      {data.values.length === 0 && (
        <Box
          sx={{
            width: "100%",
            height: { height },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CustomTypography variant="heading8" color="textSecondary">
            Nothing to show for the selected filters
          </CustomTypography>
        </Box>
      )}
    </Card>
  );
}
